import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CatalogFilters } from './CatalogFilters';

type CatalogFilterState = {
    categories?: string[];
    ageRanges?: string[];
    brands?: string[];
    query?: string;
    isAvailable?: boolean;
};

interface MobileFilterDrawerProps {
    filters: CatalogFilterState;
    onChange: (filters: CatalogFilterState) => void;
    onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    activeFilterCount?: number;
    locationId?: string;
}

export const MobileFilterDrawer = ({ filters, onChange, onSearchChange, activeFilterCount = 0, locationId }: MobileFilterDrawerProps) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full border-border/80 bg-background px-4 lg:hidden"
                aria-label={`Open filters${activeFilterCount > 0 ? `, ${activeFilterCount} active` : ''}`}
            >
                <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                        {activeFilterCount}
                    </span>
                )}
            </Button>

            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-foreground/25 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-50 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative mr-auto flex h-full w-full max-w-sm flex-col overflow-y-auto border-r border-border bg-[linear-gradient(180deg,rgba(255,255,255,1)_0%,rgba(248,243,237,1)_100%)] py-4 pb-6 shadow-[0_24px_60px_rgba(54,49,43,0.18)]">
                                <div className="flex items-center justify-between border-b border-border px-4 pb-4">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Narrow your results</p>
                                        <Dialog.Title className="mt-1 font-display text-2xl text-foreground">
                                            Filters
                                        </Dialog.Title>
                                    </div>
                                    <button
                                        type="button"
                                        className="-mr-2 flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                        onClick={() => setOpen(false)}
                                        aria-label="Close filters"
                                    >
                                        <X className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto px-4 py-5">
                                    {/* Clear-all affordance comes from the embedded <CatalogFilters /> (inline chip) and the sticky footer button below */}
                                    <CatalogFilters filters={filters} onChange={onChange} onSearchChange={onSearchChange} locationId={locationId} />
                                </div>

                                <div className="sticky bottom-0 border-t border-border bg-background/92 px-4 py-4 backdrop-blur-sm space-y-2">
                                    <Button
                                        className="w-full"
                                        onClick={() => setOpen(false)}
                                    >
                                        See matching items
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="w-full"
                                        onClick={() => {
                                            onChange({});
                                            setOpen(false);
                                        }}
                                    >
                                        Clear all filters
                                    </Button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    );
};
